import React from 'react';
import { cn } from '@/lib/utils';
import { ThemeName } from '@/utils/storage';
import { motion } from 'framer-motion';

interface ThemeSelectorProps {
  currentTheme: ThemeName;
  onThemeChange: (theme: ThemeName) => void;
}

const themes: {
  name: ThemeName;
  label: string;
  emoji: string;
  bgClass: string;
  borderClass: string;
  iconClass: string;
}[] = [
  {
    name: 'robots',
    label: 'Robots',
    emoji: '🤖',
    bgClass: 'from-blue-500 to-blue-700',
    borderClass: 'border-blue-500',
    iconClass: 'text-blue-500'
  },
  {
    name: 'unicorn',
    label: 'Unicorn',
    emoji: '🦄',
    bgClass: 'from-purple-500 to-pink-500',
    borderClass: 'border-purple-500',
    iconClass: 'text-purple-500'
  },
  {
    name: 'space',
    label: 'Space',
    emoji: '🚀',
    bgClass: 'from-indigo-700 to-violet-800',
    borderClass: 'border-indigo-500',
    iconClass: 'text-indigo-500'
  },
  {
    name: 'ocean',
    label: 'Ocean',
    emoji: '🌊',
    bgClass: 'from-cyan-600 to-cyan-800',
    borderClass: 'border-cyan-500',
    iconClass: 'text-cyan-600'
  },
  {
    name: 'forest',
    label: 'Forest',
    emoji: '🌲',
    bgClass: 'from-green-600 to-emerald-700',
    borderClass: 'border-green-500',
    iconClass: 'text-green-600'
  },
  {
    name: 'sunset',
    label: 'Sunset',
    emoji: '🌅',
    bgClass: 'from-orange-500 to-pink-600',
    borderClass: 'border-orange-500',
    iconClass: 'text-orange-500'
  },
  {
    name: 'volcano',
    label: 'Volcano',
    emoji: '🌋',
    bgClass: 'from-red-600 to-orange-700',
    borderClass: 'border-red-600',
    iconClass: 'text-red-600'
  },
  {
    name: 'trucks',
    label: 'Monster Trucks',
    emoji: '🏎️',
    bgClass: 'from-lime-500 to-green-700',
    borderClass: 'border-lime-500',
    iconClass: 'text-lime-600'
  },
  {
    name: 'ninja',
    label: 'Ninja',
    emoji: '🥷',
    bgClass: 'from-slate-800 to-red-900',
    borderClass: 'border-red-700',
    iconClass: 'text-red-600'
  },
  {
    name: 'spongebob',
    label: 'Undersea',
    emoji: '🧽',
    bgClass: 'from-yellow-400 to-amber-500',
    borderClass: 'border-yellow-400',
    iconClass: 'text-amber-700'
  },
  {
    name: 'dinosaurs',
    label: 'Dinosaurs',
    emoji: '🦖',
    bgClass: 'from-emerald-600 to-lime-700',
    borderClass: 'border-emerald-500',
    iconClass: 'text-emerald-700 dark:text-emerald-300'
  },
  {
    name: 'candy',
    label: 'Candy Land',
    emoji: '🍭',
    bgClass: 'from-pink-500 to-violet-600',
    borderClass: 'border-pink-500',
    iconClass: 'text-pink-700 dark:text-pink-300'
  }
];

const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  currentTheme,
  onThemeChange,
}) => {
  const handleThemeChange = (theme: ThemeName) => {
    onThemeChange(theme);

    const event = new CustomEvent('themeChanged', {
      detail: { theme }
    });
    document.dispatchEvent(event);
  };

  return (
    <div className="p-4 rounded-3xl bg-background border border-border">
      <h3 className="text-lg font-bold mb-4">Choose Your Theme</h3>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {themes.map((theme) => (
          <motion.div
            key={theme.name}
            className="flex flex-col items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              className={cn(
                "w-24 h-24 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 overflow-hidden relative",
                `bg-gradient-to-br ${theme.bgClass}`,
                currentTheme === theme.name ?
                  `ring-4 ring-offset-2 ring-offset-background ${theme.borderClass} shadow-xl` :
                  "ring-1 ring-white/20 dark:ring-gray-800"
              )}
              onClick={() => handleThemeChange(theme.name)}
              aria-label={`Select ${theme.label} theme`}
              aria-pressed={currentTheme === theme.name}
            >
              {currentTheme === theme.name && (
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-1 right-2 h-2 w-2 bg-white rounded-full animate-pulse opacity-70"></div>
                  <div className="absolute bottom-3 left-3 h-1 w-1 bg-white rounded-full animate-ping opacity-70"></div>
                  <div className="absolute top-1/2 right-4 h-1.5 w-1.5 bg-white rounded-full animate-pulse opacity-70"></div>
                </div>
              )}

              <span
                role="img"
                aria-label={theme.label}
                className={cn(
                  "text-4xl text-white drop-shadow-md transition-all duration-300",
                  currentTheme === theme.name && "animate-float"
                )}
              >
                {theme.emoji}
              </span>
            </button>

            <div className="text-center mt-3">
              <span className={cn(
                "text-sm font-medium transition-all duration-300 block",
                currentTheme === theme.name
                  ? `${theme.iconClass} font-bold`
                  : "text-muted-foreground"
              )}>
                {theme.label}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ThemeSelector;
